// src/hooks/useRemoteNav.ts
import { useEffect, useState } from "react";
import type { AppConfig, NavItem, NavSection } from "@/registry/appRegistry";
import { REMOTE_MAP } from "@/registry/remoteMap";

interface LoadedNav {
  navItems: NavItem[];
  subNavSections: NavSection[];
  error: boolean;
}

interface RemoteNavState extends LoadedNav {
  loading: boolean;
}

// Cache only successful loads. Failed imports delete their cache entry
// so the next call (e.g. re-navigating to the app) retries the import.
const navCache = new Map<string, Promise<LoadedNav>>();

function loadNav(appId: string, app: AppConfig): Promise<LoadedNav> {
  const cached = navCache.get(appId);
  if (cached) return cached;

  const remote = REMOTE_MAP[appId];
  if (!remote) {
    const result: LoadedNav = {
      navItems: app.navItems ?? [],
      subNavSections: app.subNavSections ?? [],
      error: false,
    };
    const promise = Promise.resolve(result);
    navCache.set(appId, promise);
    return promise;
  }

  const loadPromise = remote.NavConfig()
    .then((mod): LoadedNav => ({
      navItems: mod.default.navItems,
      subNavSections: mod.default.subNavSections,
      error: false,
    }))
    .catch((err): LoadedNav => {
      navCache.delete(appId);
      console.warn(`[useRemoteNav] Failed to load NavConfig for "${appId}"`, err);
      return {
        // Migration bridge: use static registry nav for apps that
        // haven't added ./NavConfig yet. Once all apps export NavConfig,
        // these fields and this fallback path get removed.
        navItems: app.navItems ?? [],
        subNavSections: app.subNavSections ?? [],
        error: true,
      };
    });

  navCache.set(appId, loadPromise);
  return loadPromise;
}

export function useRemoteNav(app: AppConfig | undefined): RemoteNavState {
  const [state, setState] = useState<RemoteNavState>(() => ({
    // Initialize with static nav (if any) so there's no flash of empty nav
    // for apps still using registry-based nav during migration.
    navItems: app?.navItems ?? [],
    subNavSections: app?.subNavSections ?? [],
    loading: !!app,
    error: false,
  }));

  useEffect(() => {
    if (!app) {
      setState({ navItems: [], subNavSections: [], loading: false, error: false });
      return;
    }

    let cancelled = false;

    loadNav(app.id, app).then((nav) => {
      if (!cancelled) {
        setState({ ...nav, loading: false });
      }
    });

    return () => { cancelled = true; };
  }, [app?.id]);

  return state;
}
